<template>
  <v-card class="ma-3">
    <v-card-title class="pt-3 pb-0 text-left">
      {{ $t('developerPanel.createEmTitle') }}
    </v-card-title>
    <v-card-text class="pt-4">
      <v-form ref="createForm">
        <v-select
          v-model="device"
          :label="$t('developerPanel.labels.deviceType')"
          :items="deviceTypes"
          item-title="name"
          item-value="id"
          return-object
          variant="outlined"
        />
        <v-text-field
          v-model="lastSerial"
          :label="$t('developerPanel.labels.serial')"
          variant="outlined"
          :rules="[validationRules.serialNumber]"
          @update:model-value="validateSerial"
        />
        <v-checkbox v-model="bulk" :label="$t('developerPanel.labels.multiple')" />
        <v-text-field
          v-if="bulk"
          v-model="quantity"
          type="number"
          :label="$t('developerPanel.labels.quantity')"
          variant="outlined"
          :rules="[validationRules.quantity]"
          @update:model-value="validateBulk"
        />
      </v-form>
      <v-btn :disabled="!isSerialValid || (bulk && !isBulkValid)" @click="createEmClick">{{
        $t('buttons.create')
      }}</v-btn>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { FusionStores } from '@fusion/saas-components-v3'
import { NAMESPACES } from '@/store/types'
import { DEVELOPER_PANEL_ACTIONS } from '@/store/developerpanel'
import type { NewEmulator } from '@/types/emulator'
import { HTTPStatus } from '@/api'
import { DEVICES, type DeviceOption } from './constants'

export default defineComponent({
  name: 'EmulatorPanel',

  setup() {
    const notifications = FusionStores.notifications()
    return { notifications }
  },

  data() {
    return {
      device: (DEVICES as DeviceOption[])[0],
      devices: [] as NewEmulator[],
      deviceTypes: DEVICES as DeviceOption[],
      lastSerial: '' as string,
      quantity: 2 as number,
      bulk: false as boolean,
      validationRules: {
        serialNumber: (value: string): string | boolean =>
          /^(?!1[5-9]|[2-4]\d|50)\d{2}\d{5}$/.test(value) ||
          this.$t('developerPanel.invalidSerial'),
        quantity: (value: number): string | boolean =>
          (value >= 2 && value <= 100) || this.$t('developerPanel.invalidQuantity')
      },
      isSerialValid: false as boolean,
      isBulkValid: true as boolean
    }
  },

  methods: {
    validateSerial(): void {
      this.isSerialValid = this.validationRules.serialNumber(this.lastSerial) === true
    },

    validateBulk(): void {
      this.isBulkValid = this.validationRules.quantity(this.quantity) === true
    },

    async createEmClick(): Promise<void> {
      this.setDevices()

      const response = await this.$api.createEmulator(this.devices)
      if (response.status === HTTPStatus.Created) {
        const res = await this.$api.getAllEmulators()
        if (res.status === HTTPStatus.OK) {
          const data = await res.json()
          this.$store.dispatch(
            `${NAMESPACES.DEVELOPER_PANEL}/${DEVELOPER_PANEL_ACTIONS.SET_EMULATORS}`,
            data
          )
        } else {
          const message = {
            text: this.$t('developerPanel.apiCallFailed'),
            type: 'warning',
            timeout: -1,
            buttonText: this.$t('buttons.close'),
            status: res.status
          }
          this.notifications.addMessage(message)
        }
      }
    },

    setDevices(): void {
      const device: NewEmulator = {
        model: this.device.model_number,
        serial: this.device.model_number + this.lastSerial
      }
      if (this.device.sub_model) device.sub_model = this.device.sub_model

      const devices: NewEmulator[] = [device]

      if (this.bulk) {
        for (let i = 1; i < this.quantity; i++) {
          const bulk: NewEmulator = {
            model: this.device.model_number,
            serial: this.device.model_number + String(+this.lastSerial + i).padStart(7, '0')
          }
          if (this.device.sub_model) bulk.sub_model = this.device.sub_model
          devices.push(bulk)
        }
      }
      this.devices = devices
    }
  }
})
</script>
